import { BEATS, partForBeat } from '@/story/beats';
import { type LedgerSnapshot } from '@/story/ledger';
import { DEFAULT_OUT } from '@/story/act3/tally';
import { readJson, removeRaw, writeRaw } from './storage';

/**
 * The save: a beat and its one-based room part.
 *
 * D-053: *"A section boundary commits. Finishing a beat writes the checkpoint.
 * Nothing else does; there is no autosave ticking mid-beat."* This is that, and
 * the whole of it — **a checkpoint is the beat part to resume at**. Single-room
 * beats remain part one, so existing saves and URLs keep their compact shape.
 *
 * ── Why there is no position, no pose and no world state ────────────────────
 *
 * Because a beat re-enters through the same door every time. `main.ts` hands
 * over between beats with `window.location.replace('?beat=N')` and argues the
 * case in place: beats do not share a world, and an in-place swap would be a
 * second construction path for a state that today has exactly one — `boot()`.
 * Resuming is therefore *identical* to handing over, and a save that restored a
 * position mid-beat would be inventing the second path that decision refuses.
 *
 * It is also what makes the format safe to have designed now rather than after
 * beat 8 (D-102): there is nothing in it to guess wrong. When a companion, a
 * herd and a carried object exist, they are fields **added to a version 1 file
 * that already reads defensively**, not a format retrofitted onto a shipped
 * game.
 *
 * ── The ordering constraint, restated because it is the one to get wrong ────
 *
 * D-053: the pause-menu restart *"must therefore exist before the first
 * checkpoint is ever written — not after"*. Shipping the write first and the
 * way back next session produces a build that can only be played from wherever
 * the reviewer last got to. `restart()` is in this file for that reason: the
 * two halves cannot be separated by a commit if they are the same module.
 */

const PROGRESS_KEY = 'rosalie:progress';

/**
 * The format's version, written on every commit and checked on every read.
 *
 * An unknown version is treated as no save at all rather than as an error. The
 * player loses their place once, silently, at an upgrade — against a build that
 * throws on boot for everybody who played the previous one.
 */
const VERSION = 1;

/** Kept as data rather than `null`: a corrupt note must not discard a valid checkpoint. */
export const EMPTY_LEDGER_SNAPSHOT: LedgerSnapshot = { observed: [], wash: {} };

/** A full localStorage origin is roughly 5 MB; this is a storage bound, not a design one. */
const MAX_PERSISTED_WASH_CELLS = 4000;

export interface Progress {
  /** The beat Continue resumes at. Always a key of `BEATS`, and never 0. */
  readonly beat: number;
  /** The one-based room part Continue resumes at. */
  readonly part: number;
  /** Whether the last committed boundary was the END of the act. */
  readonly finished: boolean;
  /** Observations and route wash that persist across the beat boundary. */
  readonly ledger: LedgerSnapshot;
  /** Beat 10's live culvert result, carried to the office rather than authored there. */
  readonly out: number;
}

/** Old saves predate the tally, so absence preserves the authored twelve-out baseline. */
export { DEFAULT_OUT };

/** `null` preserves the distinction between a valid zero and an invalid URL value. */
export function validatedOut(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 12 ? value : null;
}

/** The single legacy/default boundary for every tally consumer. */
export function effectiveOut(value: unknown): number {
  return validatedOut(value) ?? DEFAULT_OUT;
}

function copyEmptyLedger(): LedgerSnapshot {
  return { observed: [], wash: {} };
}

/**
 * An array of non-empty strings, or `null` for anything else.
 *
 * Written as one narrowing helper rather than as `Array.isArray` plus an
 * `.every` guard because **`Array.isArray(x: unknown)` narrows to `any[]`, not
 * to `unknown[]`** — so the obvious spelling type-checks, passes review, and is
 * an `any` in the one function in this file whose whole job is to distrust what
 * it is handed. `no-unsafe-assignment` caught it; a reader would not have.
 */
function nonEmptyStrings(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  const items: string[] = [];
  for (const item of value as readonly unknown[]) {
    if (typeof item !== 'string' || item.length === 0) return null;
    items.push(item);
  }
  return items;
}

function readLedger(value: unknown): LedgerSnapshot {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return copyEmptyLedger();
  const record = value as Readonly<Record<string, unknown>>;
  const observed = nonEmptyStrings(record['observed']);
  const wash = record['wash'];
  if (observed === null || typeof wash !== 'object' || wash === null || Array.isArray(wash)) {
    return copyEmptyLedger();
  }

  const checkedWash: Record<string, readonly string[]> = {};
  for (const [zone, rawCells] of Object.entries(wash)) {
    const cells = nonEmptyStrings(rawCells);
    // One damaged zone is not a reason to erase notes from every other room.
    // This is the narrowest recovery that still refuses to feed an unchecked
    // array into a ledger restored on the fixed-step path.
    if (zone.length === 0 || cells === null) continue;
    checkedWash[zone] = cells;
  }
  return { observed, wash: checkedWash };
}

function compareCellKeys(a: string, b: string): number {
  const aComma = a.indexOf(',');
  const bComma = b.indexOf(',');
  const aX = Number(a.slice(0, aComma));
  const aZ = Number(a.slice(aComma + 1));
  const bX = Number(b.slice(0, bComma));
  const bZ = Number(b.slice(bComma + 1));
  return aZ - bZ || aX - bX || (a < b ? -1 : a > b ? 1 : 0);
}

/**
 * Trim the wash to the storage cap, **sharing the budget between rooms**.
 *
 * The first version walked the zones in sorted order and gave each one whatever
 * was left, which meant a long enough playthrough silently erased whole rooms
 * from the far end of the alphabet — deterministically, so no gate would ever
 * have caught it wobbling, and invisibly, because a missing sheet looks exactly
 * like a room she has not walked. Found on independent review.
 *
 * Every room gets an equal share; rooms that want less than their share hand the
 * remainder back to the ones that want more. **A player loses the edges of the
 * rooms they explored most, never a room entire.**
 */
function persistedLedger(snapshot: LedgerSnapshot): LedgerSnapshot {
  const wash: Record<string, readonly string[]> = {};
  const zones = Object.keys(snapshot.wash).sort();
  const sorted = new Map<string, string[]>();
  for (const zone of zones) {
    const cells = snapshot.wash[zone];
    if (cells !== undefined && cells.length > 0) sorted.set(zone, [...cells].sort(compareCellKeys));
  }

  let remaining = MAX_PERSISTED_WASH_CELLS;
  let unserved = sorted.size;
  for (const [zone, cells] of sorted) {
    if (unserved === 0) break;
    // `ceil`, so the last room is never starved by integer division.
    const share = Math.ceil(remaining / unserved);
    const kept = cells.slice(0, share);
    if (kept.length > 0) wash[zone] = kept;
    remaining -= kept.length;
    unserved -= 1;
  }
  return { observed: [...snapshot.observed], wash };
}

/**
 * The last committed boundary, or `null` if there is not one.
 *
 * Every field is re-validated. The thing on the other side of this call is a
 * string a browser has been holding since a build that may no longer exist, and
 * **a beat number that is no longer in the table must not boot** — `main.ts`
 * throws on an unknown beat, which as a resume path would mean a save that
 * bricks the game for the one player who has it.
 */
export function readProgress(): Progress | null {
  const stored = readJson(PROGRESS_KEY);
  if (!stored) return null;
  if (stored['v'] !== VERSION) return null;

  const beat = stored['beat'];
  if (typeof beat !== 'number' || !Number.isInteger(beat) || beat === 0) return null;
  const entry = BEATS[beat];
  if (!entry) return null;

  // V1 saves predate multipart beats. Absence therefore means the first room,
  // while a present but malformed part is corrupt state rather than a request we
  // may guess at: falling back here could resume somebody in the wrong room.
  const part = stored['part'] === undefined ? 1 : stored['part'];
  if (typeof part !== 'number' || !Number.isInteger(part) || partForBeat(entry, part) === null) {
    return null;
  }

  return { beat, part, finished: stored['finished'] === true, ledger: readLedger(stored['ledger']), out: effectiveOut(stored['out']) };
}

/**
 * Commit the boundary at the end of `finishedBeat`.
 *
 * The checkpoint written is the **successor part**, not the section just played:
 * a player who finishes beat 4 and closes the tab has finished beat 4, and
 * resuming should not make them play it again. A final part enters its next beat
 * at part one; where there is no next beat the final part is kept so Continue
 * has somewhere to go, and `finished` is what the title screen reads to offer
 * the credits.
 *
 * Beat 0 never commits. It is the no-beat fixture every controller and perf spec
 * boots (see `BEATS`), it has no story to finish, and a checkpoint naming it
 * would resume into a room with nothing in it.
 */
export function commitBoundary(finishedBeat: number, finishedPart: number, ledger: LedgerSnapshot, outcome?: { readonly out?: number }): void {
  const entry = BEATS[finishedBeat];
  if (!entry || finishedBeat === 0) return;
  if (partForBeat(entry, finishedPart) === null) return;

  // A boundary owns only its supplied outcome; later ordinary boundaries retain
  // prior results instead of erasing the number the office is about.
  const out = effectiveOut(outcome?.out ?? readProgress()?.out);
  const nextPart = finishedPart + 1;
  const progress: Progress =
    partForBeat(entry, nextPart) !== null
      ? { beat: finishedBeat, part: nextPart, finished: false, ledger: persistedLedger(ledger), out }
      : entry.next === undefined
        ? { beat: finishedBeat, part: finishedPart, finished: true, ledger: persistedLedger(ledger), out }
        : { beat: entry.next, part: 1, finished: false, ledger: persistedLedger(ledger), out };

  writeRaw(PROGRESS_KEY, JSON.stringify({ v: VERSION, ...progress }));
}

/**
 * Throw the save away. The pause menu's *restart completely*, and the title
 * screen's *New Game*.
 *
 * **Settings are not touched, and that is decided rather than overlooked**
 * (D-124). They live under their own key for this reason: nobody who turned
 * Gentle Mode on asked to have it turned off by starting over, and re-answering
 * the content notice on every restart is friction aimed squarely at the person
 * doing the testing.
 */
export function clearProgress(): void {
  removeRaw(PROGRESS_KEY);
}
